import { useEffect, useState } from 'react';

/**
 * Custom React hook to synchronize a controlled input value with an external state.
 *
 * This hook manages a local input state (`inputDisplayed`) that is initialized from the external `readingInput` value.
 * It ensures that changes to the local input are propagated to the external state via `setReadingInput`, and vice versa.
 *
 * If `inputDisplayed` is set to `undefined`, it will reset to the current `readingInput`.
 * Otherwise, when `inputDisplayed` changes and is not `null`, it updates the external state.
 *
 * The motivation for this hook is to deal with the <input> component when it has the type 'number'.
 * For the input to show numbers that are not valid , it will need the value to be undefined.
 * i.e. for the input to show '3.' it needs value={undefined}, and this should not trigger a
 * onReadingChange thus setReadingInput should not be called when `inputDisplayed` is `undefined`.
 *
 * @param params - An object containing:
 *   @param readingInput - The current value of the input from external state.
 *   @param setReadingInput - Setter function to update the external input value.
 * @returns An object containing:
 *   @property inputDisplayed - The value currently displayed in the input field.
 *   @property setInputDisplayed - Setter function to update the displayed input value.
 */
export const useValuesInInput = ({
  readingInput,
  setReadingInput,
}: {
  readingInput: string | undefined;
  setReadingInput: (value: string | undefined) => void;
}) => {
  const [inputDisplayed, setInputDisplayed] = useState<
    string | undefined | null
  >(readingInput);

  useEffect(() => {
    if (inputDisplayed !== null && inputDisplayed !== readingInput) {
      if (inputDisplayed === undefined) {
        setInputDisplayed(readingInput);
      } else {
        setReadingInput(inputDisplayed);
      }
    }
  }, [inputDisplayed, readingInput, setReadingInput]);

  return {
    inputDisplayed,
    setInputDisplayed,
  };
};
